/**
 * Inbox Chatbox Component
 * ========================
 * 
 * Chat interface for inbox messages.
 * Displays chat history and allows sending messages.
 */

import React, { useState, useEffect, useRef } from 'react';
import MentionAutocomplete from './MentionAutocomplete';
import ViewParticipantsModal from './ViewParticipantsModal';
import './InboxChatbox.scss';

interface ChatMessage {
  message: string;
  sender: 'system' | 'user';
  sender_id?: string;
  sender_name: string;
  timestamp?: string;
  read: boolean;
}

interface InboxChatboxProps {
  inboxId: string | null;
  chatHistory: Array<{
    message: string;
    sender: 'system' | 'user';
    sender_id?: string;
    sender_name: string;
    timestamp?: string;
  }>;
  onSendMessage: (message: string, mentionedUserIds?: string[]) => Promise<void>;
  isLoading?: boolean;
  currentUserId?: string;
  requestId?: string;
  rfqId?: string; // RFQ ID to display
  participants?: Array<{ user_id: string; name: string; email: string }>;
  onAddParticipant?: () => void;
}

const InboxChatbox: React.FC<InboxChatboxProps> = ({
  inboxId,
  chatHistory,
  onSendMessage,
  isLoading = false,
  currentUserId,
  requestId,
  rfqId,
  participants: propParticipants,
  onAddParticipant
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [showViewParticipantsModal, setShowViewParticipantsModal] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const participantsContainerRef = useRef<HTMLDivElement>(null);
  const participantsListRef = useRef<HTMLDivElement>(null);
  const highlighterRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (highlighterRef.current) {
      highlighterRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  // Use participants from props (from allowed_user_ids) if available, otherwise extract from chat history
  const participants = React.useMemo(() => {
    let participantList: Array<{ name: string; user_id: string; email: string }> = [];

    if (propParticipants && propParticipants.length > 0) {
      participantList = propParticipants;
    } else {
      // Fallback: extract from chat history
      const participantMap = new Map<string, { name: string; user_id: string; email: string }>();

      chatHistory.forEach((msg) => {
        const key = msg.sender_id ? String(msg.sender_id) : msg.sender_name;
        if (!participantMap.has(key)) {
          participantMap.set(key, {
            name: msg.sender_name,
            user_id: msg.sender_id || '',
            email: '' // Fallback for chat history derived participants
          });
        }
      });

      participantList = Array.from(participantMap.values());
    }

    // Extract mentioned user names from all messages
    const mentionedNames = new Set<string>();
    chatHistory.forEach((msg) => {
      // Parse @mentions from message text
      const mentionRegex = /@([^\s@]+)/g;
      let match;
      while ((match = mentionRegex.exec(msg.message)) !== null) {
        mentionedNames.add(match[1]);
      }
    });

    // Don't mark participants - we only highlight mentions in message text, not in participant list
    return participantList;
  }, [propParticipants, chatHistory]);

  // Check if participants list overflows and calculate visible participants
  const [visibleParticipants, setVisibleParticipants] = useState<Array<{ name: string; user_id: string; email: string }>>([]);
  const [showEllipsis, setShowEllipsis] = useState(false);

  useEffect(() => {
    if (!participantsContainerRef.current || !participantsListRef.current || participants.length === 0) {
      setVisibleParticipants(participants);
      setShowEllipsis(false);
      return;
    }

    const checkOverflow = () => {
      const container = participantsContainerRef.current;
      const list = participantsListRef.current;

      if (!container || !list) return;

      // Temporarily show all participants to measure
      list.style.display = 'flex';
      list.style.flexWrap = 'nowrap';

      const containerWidth = container.offsetWidth;
      const labelWidth = container.querySelector('.inbox-chatbox__participants-label')?.getBoundingClientRect().width || 0;
      const availableWidth = containerWidth - labelWidth - 20; // 20px for gap

      // Check if all participants fit
      const allFit = list.scrollWidth <= availableWidth;

      if (allFit) {
        setVisibleParticipants(participants);
        setShowEllipsis(false);
      } else {
        // Find how many participants fit
        let visibleCount = 0;
        const ellipsisWidth = 20; // Approximate width of "..."

        // Create a temporary container to measure
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.visibility = 'hidden';
        tempContainer.style.fontSize = '0.875rem';
        tempContainer.style.display = 'flex';
        tempContainer.style.gap = '0.25rem';
        document.body.appendChild(tempContainer);

        let currentWidth = 0;
        for (let i = 0; i < participants.length; i++) {
          const tempSpan = document.createElement('span');
          tempSpan.textContent = participants[i].name;
          tempContainer.appendChild(tempSpan);
          const spanWidth = tempSpan.offsetWidth;

          // Add separator width if not last
          const separatorWidth = i < participants.length - 1 ? 8 : 0;

          if (currentWidth + spanWidth + separatorWidth + ellipsisWidth <= availableWidth) {
            currentWidth += spanWidth + separatorWidth;
            visibleCount++;
          } else {
            break;
          }
        }

        document.body.removeChild(tempContainer);

        setVisibleParticipants(participants.slice(0, visibleCount));
        setShowEllipsis(visibleCount < participants.length);
      }
    };

    // Check on mount and resize
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (participantsContainerRef.current) {
      resizeObserver.observe(participantsContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [participants, inboxId]);

  // Restore scroll position when inboxId changes or chatHistory loads (coming back from RFQ)
  const scrollRestoredRef = useRef<string | null>(null);
  useEffect(() => {
    if (inboxId && chatHistory.length > 0) {
      // Only restore once per inboxId change
      if (scrollRestoredRef.current !== inboxId) {
        const savedScroll = localStorage.getItem(`inbox_scroll_${inboxId}`);
        if (savedScroll) {
          // Wait for DOM to be ready, then restore scroll position
          setTimeout(() => {
            const chatContainer = document.querySelector('.inbox-chatbox__messages');
            if (chatContainer) {
              chatContainer.scrollTop = parseInt(savedScroll, 10);
              // Mark as restored
              scrollRestoredRef.current = inboxId;
              // Remove from localStorage after restoring
              localStorage.removeItem(`inbox_scroll_${inboxId}`);
            }
          }, 300);
          return; // Don't auto-scroll if we're restoring a saved position
        }
      }
    }

    // Reset scroll restored flag when inboxId changes
    if (scrollRestoredRef.current !== inboxId) {
      scrollRestoredRef.current = null;
    }
  }, [inboxId, chatHistory.length]); // Trigger when inboxId changes or chatHistory loads

  // Auto-scroll to bottom when new messages arrive, but only if user is already at bottom
  // This prevents scrolling when user is reading older messages
  useEffect(() => {
    if (chatEndRef.current && chatHistory.length > 0) {
      // Don't auto-scroll if we're restoring a saved scroll position
      if (scrollRestoredRef.current === inboxId) {
        return;
      }

      const chatContainer = chatEndRef.current.parentElement;
      if (chatContainer) {
        // Check if user is near bottom (within 100px)
        const isNearBottom = chatContainer.scrollHeight - chatContainer.scrollTop - chatContainer.clientHeight < 100;
        if (isNearBottom) {
          // Only scroll if user is already at bottom (new message arrived)
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    }
  }, [chatHistory.length, inboxId]); // Trigger on new messages

  const handleSend = async () => {
    if (!message.trim() || isSending || !inboxId) return;

    setIsSending(true);
    try {
      // Find all mentioned users in the message
      const mentionedUserIds: string[] = [];

      // Match @Name... patterns
      // We can reuse the same logic or loop through participants
      // It's safer to loop through participants and check if their name is mentioned

      if (participants.length > 0) {
        participants.forEach(p => {
          const escapedName = p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Check for @Name as a whole word (followed by space or punctuation or end of string)
          const pattern = new RegExp(`@${escapedName}(?=\\s|$|,|\\.|!|\\?|:|\\n)`, 'i');
          if (pattern.test(message)) {
            mentionedUserIds.push(p.user_id);
          }
        });
      }

      await onSendMessage(message.trim(), mentionedUserIds);
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !showMentionAutocomplete) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    const newCursorPosition = e.target.selectionStart;

    setMessage(newMessage);
    setCursorPosition(newCursorPosition);

    // Check if @ is being typed - show immediately when @ is pressed
    const textBeforeCursor = newMessage.substring(0, newCursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Show autocomplete immediately when @ is typed and there's no space after it
      if (!afterAt.includes(' ') && participants.length > 0) {
        setShowMentionAutocomplete(true);
      } else {
        setShowMentionAutocomplete(false);
      }
    } else {
      setShowMentionAutocomplete(false);
    }
  };

  const handleMentionSelect = (participant: { user_id: string; name: string; email: string }) => {
    if (!textareaRef.current) return;

    const textBeforeCursor = message.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterCursor = message.substring(cursorPosition);
      const newMessage =
        message.substring(0, lastAtIndex) +
        `@${participant.name} ` +
        textAfterCursor;

      setMessage(newMessage);
      setShowMentionAutocomplete(false);

      // Set cursor position after mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = lastAtIndex + participant.name.length + 2; // +2 for @ and space
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    }
  };

  const handleMentionClose = () => {
    setShowMentionAutocomplete(false);
  };

  const formatTime = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  // Format message text with highlighted mentions - handles multi-word names
  const formatMessage = (messageText: string) => {
    if (!messageText) return '';

    // Get all participant names to match against
    const participantNames = participants.map(p => p.name);

    // Sort by length (longest first) to match full names before partial matches
    const sortedNames = [...participantNames].sort((a, b) => b.length - a.length);

    const parts: Array<string | React.ReactElement> = [];
    let lastIndex = 0;

    // Find all mentions in the message
    const mentionPositions: Array<{ start: number; end: number; name: string }> = [];

    for (const name of sortedNames) {
      // Create regex pattern that matches @ followed by the full name (case insensitive)
      // The name can have spaces, so we need to match the entire name including spaces
      // Escape special regex characters, but preserve spaces as-is
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match @ followed by the full name (including spaces), ending at whitespace, punctuation, or end of string
      // Use word boundary or lookahead to ensure we match the complete name
      const pattern = new RegExp(`@${escapedName}(?=\\s|$|,|\\.|!|\\?|:|\\n)`, 'gi');
      let match;

      while ((match = pattern.exec(messageText)) !== null) {
        const fullMention = match[0]; // This includes @ and the full name
        mentionPositions.push({
          start: match.index,
          end: match.index + fullMention.length,
          name: fullMention
        });
      }
    }

    // Sort mentions by position
    mentionPositions.sort((a, b) => a.start - b.start);

    // Remove overlapping mentions (keep longest)
    const filteredPositions: Array<{ start: number; end: number; name: string }> = [];
    for (const pos of mentionPositions) {
      const overlaps = filteredPositions.some(existing =>
        (pos.start >= existing.start && pos.start < existing.end) ||
        (pos.end > existing.start && pos.end <= existing.end) ||
        (pos.start <= existing.start && pos.end >= existing.end)
      );

      if (!overlaps) {
        filteredPositions.push(pos);
      }
    }

    // Build parts array with highlighted mentions
    for (const mention of filteredPositions) {
      // Add text before mention
      if (mention.start > lastIndex) {
        parts.push(messageText.substring(lastIndex, mention.start));
      }

      // Add highlighted mention
      parts.push(
        <span key={mention.start} className="inbox-chatbox__mention">
          {mention.name}
        </span>
      );

      lastIndex = mention.end;
    }

    // Add remaining text
    if (lastIndex < messageText.length) {
      parts.push(messageText.substring(lastIndex));
    }

    return parts.length > 0 ? parts : messageText;
  };

  if (!inboxId) {
    return (
      <div className="inbox-chatbox inbox-chatbox--empty">
        <div className="inbox-chatbox__empty-message">
          <p>Select an inbox item to view chat</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inbox-chatbox">
      {inboxId && (
        <div className="inbox-chatbox__header">
          <h3 className="inbox-chatbox__title">Team Chat</h3>
          <div className="inbox-chatbox__header-row">
            {participants.length > 0 && (
              <div
                className="inbox-chatbox__participants"
                ref={participantsContainerRef}
                onClick={() => setShowViewParticipantsModal(true)}
              >
                <span className="inbox-chatbox__participants-label">
                  Participants:
                </span>
                <div
                  className="inbox-chatbox__participants-list"
                  ref={participantsListRef}
                >
                  {visibleParticipants.map((participant, index) => (
                    <React.Fragment key={participant.user_id || index}>
                      <span className="inbox-chatbox__participant">
                        {participant.name}
                      </span>
                      {index < visibleParticipants.length - 1 && (
                        <span className="inbox-chatbox__participant-separator">,</span>
                      )}
                    </React.Fragment>
                  ))}
                  {showEllipsis && (
                    <span className="inbox-chatbox__participant-ellipsis">
                      ...
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="inbox-chatbox__messages">
        {isLoading ? (
          <div className="inbox-chatbox__loading">
            <div className="inbox-chatbox__loading-spinner" />
            <p>Loading messages...</p>
          </div>
        ) : chatHistory.length === 0 ? (
          <div className="inbox-chatbox__no-messages">
            <p>No messages yet</p>
          </div>
        ) : (
          chatHistory.map((msg, index) => {
            // Determine if this message is from current user
            const isCurrentUser = currentUserId && msg.sender_id && String(msg.sender_id) === String(currentUserId);
            const messageClass = isCurrentUser ? 'user' : 'other';

            return (
              <div
                key={index}
                className={`inbox-chatbox__message inbox-chatbox__message--${messageClass}`}
              >
                <div className="inbox-chatbox__message-content">
                  <div className="inbox-chatbox__message-header">
                    <span className="inbox-chatbox__message-sender">
                      {msg.sender_name}
                    </span>
                    {msg.timestamp && (
                      <span className="inbox-chatbox__message-time">
                        {formatTime(msg.timestamp)}
                      </span>
                    )}
                  </div>
                  <div className="inbox-chatbox__message-text">
                    {formatMessage(msg.message)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="inbox-chatbox__input">
        {/* Input Overlay Container */}
        <div className="inbox-chatbox__input-container">
          {showMentionAutocomplete && participants && participants.length > 0 && (
            <MentionAutocomplete
              participants={participants}
              message={message}
              cursorPosition={cursorPosition}
              onSelect={handleMentionSelect}
              onClose={handleMentionClose}
            />
          )}
          {/* Highlighter overlay */}
          <div className="inbox-chatbox__highlighter" ref={highlighterRef}>
            {(() => {
              if (!message) return <span>&nbsp;</span>;

              // Logic to highlight mentions including multi-word names
              // We need to match @Name Surname against participants

              const parts: React.ReactNode[] = [];
              let lastIndex = 0;

              // Sort participants by name length (descending) to match longest names first
              const sortedParticipants = [...participants].sort((a, b) => b.name.length - a.name.length);

              // Find all mentions
              const mentionPositions: Array<{ start: number; end: number; name: string }> = [];

              for (const p of sortedParticipants) {
                const escapedName = p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                // Regex to match @Name (case insensitive), ensuring it's not part of another word if possible
                // We use lookahead for end of word/string/punctuation
                const pattern = new RegExp(`@${escapedName}(?=\\s|$|,|\\.|!|\\?|:|\\n)`, 'gi');

                let match;
                while ((match = pattern.exec(message)) !== null) {
                  mentionPositions.push({
                    start: match.index,
                    end: match.index + match[0].length,
                    name: match[0] // @Name
                  });
                }
              }

              // Sort by position
              mentionPositions.sort((a, b) => a.start - b.start);

              // Filter overlaps (prioritize earlier, then longer)
              // Actually, since we want to match valid participants, simple overlap check is enough.
              const filteredPositions: Array<{ start: number; end: number; name: string }> = [];
              for (const pos of mentionPositions) {
                const isOverlapping = filteredPositions.some(existing =>
                  (pos.start < existing.end && pos.end > existing.start)
                );
                if (!isOverlapping) {
                  filteredPositions.push(pos);
                }
              }

              // Build parts
              for (const pos of filteredPositions) {
                if (pos.start > lastIndex) {
                  parts.push(message.substring(lastIndex, pos.start));
                }

                parts.push(
                  <span key={pos.start} className="inbox-chatbox__highlight-mention">
                    {pos.name}
                  </span>
                );

                lastIndex = pos.end;
              }

              // Remaining text
              if (lastIndex < message.length) {
                parts.push(message.substring(lastIndex));
              }

              return parts;
            })()}
            {/* Add a generic space at the end to prevent jumpiness when typing at end */}
            &nbsp;
          </div>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyPress={handleKeyPress}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                if (showMentionAutocomplete) {
                  e.preventDefault();
                  return;
                }
              }
              if (e.key === 'Escape' && showMentionAutocomplete) {
                e.preventDefault();
                setShowMentionAutocomplete(false);
              }
            }}
            onSelect={(e) => {
              const target = e.target as HTMLTextAreaElement;
              setCursorPosition(target.selectionStart);
            }}
            onScroll={handleScroll}
            placeholder="Type your message... Use @ to mention someone"
            rows={2}
            disabled={isSending || isLoading}
            className="inbox-chatbox__textarea"
            spellCheck={false}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!message.trim() || isSending || isLoading}
          className="inbox-chatbox__send-button"
          title="Send"
        >
          {isSending ? (
            <span className="inbox-chatbox__send-spinner">⋯</span>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="inbox-chatbox__send-icon"
            >
              <path
                d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
                fill="currentColor"
              />
            </svg>
          )}
        </button>
      </div>

      {/* View Participants Modal */}
      {participants && participants.length > 0 && (
        <ViewParticipantsModal
          isOpen={showViewParticipantsModal}
          onClose={() => setShowViewParticipantsModal(false)}
          participants={participants}
          onAddParticipant={onAddParticipant}
        />
      )}
    </div>
  );
};

export default InboxChatbox;

